import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Spec: manna-web-ui §3.5 — `?` opens a global cheat sheet listing all
// reserved keyboard bindings. Read-only; informational. Toggle via `?`.

interface Section {
  title: string;
  bindings: { keys: string; label: string }[];
}

const SECTIONS: Section[] = [
  {
    title: "Global",
    bindings: [
      { keys: "⌘/Ctrl + K", label: "Open command palette" },
      { keys: "?", label: "Open this cheat sheet" },
      { keys: "f", label: "Toggle Focus mode (collapse rails)" },
      { keys: "Esc", label: "Close drawers / dialogs / sheet" },
    ],
  },
  {
    title: "Go to…",
    bindings: [
      { keys: "g d", label: "Deals" },
      { keys: "g c", label: "Contacts" },
      { keys: "g o", label: "Organizations" },
      { keys: "g a", label: "Activities" },
      { keys: "g t", label: "Triage" },
      { keys: "g p", label: "Partners" },
      { keys: "g s", label: "Security" },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function KeyboardCheatSheet({ open, onClose }: Props) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 7 }}>
        Keyboard shortcuts
        <IconButton
          aria-label="Close cheat sheet"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {SECTIONS.map((s, i) => (
            <Box key={s.title}>
              <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 1.4 }}>
                {s.title}
              </Typography>
              <Stack spacing={0.5} mt={0.5}>
                {s.bindings.map((b) => (
                  <Stack
                    key={b.keys}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">{b.label}</Typography>
                    <Box
                      component="kbd"
                      sx={{
                        px: 1,
                        py: 0.25,
                        fontSize: 12,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                        bgcolor: "action.hover",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {b.keys}
                    </Box>
                  </Stack>
                ))}
              </Stack>
              {i < SECTIONS.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
